"use client";

// Infinite-scroll + windowed product grid.
//
// Strategy:
//  - Page 1 is rendered server-side and passed in as `initialProducts`. The
//    component first paints those in a plain CSS grid so SSR + hydration
//    match exactly (no shuffle, no LCP regression).
//  - After mount we virtualize the rows via @tanstack/react-virtual's
//    useWindowVirtualizer. Each row holds N cards where N is the
//    breakpoint's column count (2 / 3 / 4).
//  - An IntersectionObserver on a sentinel ~600px before the end of the
//    loaded list triggers a fetch of the next page from the public
//    /api/storefront/products/list endpoint, preserving filters + sort.
//  - Row heights are measured live (cards differ in height due to swatches,
//    badges, sale prices) via measureElement.
import { c as _c } from "react/compiler-runtime";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ProductCard, { type CardAction, type CardImageAspect, type ProductForCard } from "@/components/storefront/ProductCard";
import type { PaginationDTO } from "@/lib/storefront/products/listService";
type ScopeRef = {
  type: "all";
} | {
  type: "search";
  query: string;
} | {
  type: "category";
  slug: string;
} | {
  type: "collection";
  slug: string;
} | {
  type: "brand";
  slug: string;
};
type Props = {
  initialProducts: ProductForCard[];
  initialPagination: PaginationDTO;
  scope: ScopeRef;
  locale: string;
  imageAspect?: CardImageAspect;
  cardAction?: CardAction;
};
const ESTIMATED_ROW_HEIGHT = 380;
const OVERSCAN_ROWS = 4;
// Trigger the next-page fetch when the sentinel is within this margin of
// the viewport — keeps the scroll smooth without ever showing the spinner
// for more than a moment.
const SENTINEL_ROOT_MARGIN = "600px 0px";
export default function InfiniteProductList({
  initialProducts,
  initialPagination,
  scope,
  locale,
  imageAspect = "portrait",
  cardAction = "icon"
}: Props) {
  const t = useTranslations("listing");
  const sp = useSearchParams();
  const [products, setProducts] = useState<ProductForCard[]>(initialProducts);
  const [page, setPage] = useState<number>(initialPagination.page);
  const [pageCount, setPageCount] = useState<number>(initialPagination.pageCount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Reset state when the initial dataset changes (filter/sort navigation).
  // The route SSR-renders page 1 with the new filters; we re-seed from it.
  useEffect(() => {
    setProducts(initialProducts);
    setPage(initialPagination.page);
    setPageCount(initialPagination.pageCount);
    setError(null);
  }, [initialProducts, initialPagination.page, initialPagination.pageCount]);

  // Defer virtualization until after hydration so the SSR-rendered DOM
  // and the first client render match exactly (no hydration mismatch).
  useEffect(() => {
    setHydrated(true);
  }, []);
  const hasMore = page < pageCount;
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    // Forward all current URL params (sort, brand, color, etc.) so the
    // server returns a filter-consistent next page.
    for (const [k, v] of sp.entries()) {
      // Skip page — we set it explicitly below.
      if (k === "page") continue;
      params.append(k, v);
    }
    params.set("scope_kind", scope.type);
    if ("slug" in scope) params.set("scope_slug", scope.slug);
    if ("query" in scope) params.set("q", scope.query);
    params.set("locale", locale);
    params.set("page", String(page + 1));
    try {
      const res = await fetch(`/api/storefront/products/list?${params.toString()}`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = (await res.json()) as {
        products: ProductForCard[];
        pagination: PaginationDTO;
      };
      setProducts(prev => [...prev, ...data.products]);
      setPage(data.pagination.page);
      setPageCount(data.pagination.pageCount);
    } catch (_err) {
      setError(t("loadError"));
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, sp, scope, locale, page, t]);

  // ─── Responsive column count ────────────────────────────────────────
  const columns = useColumns();

  // Chunk products into rows of `columns` cards each.
  const rows = useMemo(() => {
    const out: ProductForCard[][] = [];
    for (let i = 0; i < products.length; i += columns) {
      out.push(products.slice(i, i + columns));
    }
    return out;
  }, [products, columns]);

  // ─── Virtualizer ────────────────────────────────────────────────────
  const parentRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useWindowVirtualizer({
    // Off until the plain grid has committed. The virtualizer reads its start
    // offset from window.scrollY and scrolls the window there when it attaches.
    // On a client-side navigation the first render happens while the previous
    // page is still on screen, so that offset is the old page's and the new
    // page opened part-way down. Enabled from an effect, it reads scrollY after
    // Next has reset it.
    enabled: hydrated,
    count: rows.length,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN_ROWS,
    getScrollElement: () => typeof window !== "undefined" ? window : null,
    scrollMargin: parentRef.current?.offsetTop ?? 0,
    measureElement: el => el.getBoundingClientRect().height,
    // Each row keyed by the slugs it holds so re-chunking on column change
    // doesn't blow away measured heights for already-seen products.
    getItemKey: index => rows[index]?.map(p => p.id).join("|") ?? `row-${index}`
  });

  // ─── Sentinel: trigger next page when near bottom ───────────────────
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!hydrated || !hasMore) return;
    const el_0 = sentinelRef.current;
    if (!el_0) return;
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          loadMore();
          break;
        }
      }
    }, {
      root: null,
      rootMargin: SENTINEL_ROOT_MARGIN
    });
    observer.observe(el_0);
    return () => observer.disconnect();
  }, [hydrated, hasMore, loadMore]);

  // ─── Render ─────────────────────────────────────────────────────────

  // Pre-hydration / non-JS fallback: plain CSS grid. Matches the server
  // markup exactly so React hydration finds an identical tree.
  if (!hydrated) {
    return <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {initialProducts.map((product, i_0) => <ProductCard key={product.id} product={product} locale={locale} priority={i_0 < 4} imageAspect={imageAspect} action={cardAction} />)}
      </div>;
  }
  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // The virtualizer needs runtime-computed values: an outer height equal to
  // the sum of all (estimated) row heights, and a translateY per row. These
  // can't live in a stylesheet because they change every render — they're
  // the necessary glue between layout math and the DOM. Limited inline-style
  // use here is the industry-standard pattern for @tanstack/react-virtual.
  return <div ref={parentRef} className="relative">
      <div className="relative w-full" style={{
      height: `${totalSize}px`
    }}>
        {virtualItems.map(item => {
        const row = rows[item.index];
        if (!row) return null;
        const offset = item.start - virtualizer.options.scrollMargin;
        return <div key={item.key} ref={virtualizer.measureElement} data-index={item.index} style={{
          transform: `translateY(${offset}px)`
        }} className="absolute inset-x-0 top-0 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-4">
              {row.map((product_0, colIndex) => {
            const flatIndex = item.index * columns + colIndex;
            return <ProductCard key={product_0.id} product={product_0} locale={locale} priority={flatIndex < 4} imageAspect={imageAspect} action={cardAction} />;
          })}
            </div>;
      })}
      </div>

      {hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-1 w-full" />}

      <div className="mt-6 flex flex-col items-center gap-2 text-sm">
        {loading && <output aria-live="polite" className="inline-flex items-center gap-2 text-muted-foreground">
            <span className="inline-block h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-hidden="true" />
            {t("loadingMore")}
          </output>}
        {error && !loading && <div className="inline-flex items-center gap-3 text-danger">
            <span>{error}</span>
            <button type="button" onClick={loadMore} className="text-primary hover:underline font-medium">
              {t("retry")}
            </button>
          </div>}
        {!hasMore && products.length > 0 && <span className="text-muted-foreground">{t("endOfResults")}</span>}
      </div>
    </div>;
}

// Tracks how many columns the grid is currently showing, matching the
// Tailwind breakpoints used by the static grid (grid-cols-2 / md:3 / lg:4).
function useColumns() {
  const $ = _c(2);
  const [columns, setColumns] = useState(2);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const lg = window.matchMedia("(min-width: 1024px)");
      const md = window.matchMedia("(min-width: 768px)");
      const compute = () => {
        if (lg.matches) {
          setColumns(4);
        } else {
          if (md.matches) {
            setColumns(3);
          } else {
            setColumns(2);
          }
        }
      };
      compute();
      lg.addEventListener("change", compute);
      md.addEventListener("change", compute);
      return () => {
        lg.removeEventListener("change", compute);
        md.removeEventListener("change", compute);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return columns;
}